/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import {
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import {
  closeChangePasswordModal,
  closeResetPasswordModal,
} from 'modules/modal/actions';
import { currentUrl, redirectToAccountsLogin } from 'utils/accounts';
import { messageError, messageSuccess } from 'views/ui/toast-message';

import { NO_PERMISSION_MESSAGE } from 'utils/constants/messages';
import SDK from 'utils/sdk';
import { SubmissionError } from 'redux-form';
import { createAbilityForUser } from 'utils/casl/ability-utils';
import { getMeItem } from 'modules/me/selectors';
import { push } from 'connected-react-router';
import { retrieveMe } from 'modules/me/actions';
import { CLEAR_STORE } from './types';
import {
  changePassword,
  forgotPassword,
  login,
  logout,
  resetPassword,
  verifySession,
} from './actions';

export function* loginSaga({ payload: { values: requestBody } }) {
  try {
    yield put(login.request());

    const client = yield call([SDK, 'getClient']);
    const { data } = yield call([client, 'userLogIn'], null, requestBody);
    const abilities = [];
    if (data.role.name === 'Admin') {
      abilities.push(
        { action: '*', subject: 'videos' },
        { action: '*', subject: 'library' },
        { action: '*', subject: 'monitoring' }
      );
    } else {
      abilities.push({ action: '*', subject: 'monitoring' });
    }
    const ability = createAbilityForUser(abilities);
    yield put(retrieveMe.success(data));
    yield put(login.success({ ability, tokenExpiresAt: data.tokenExpiresAt }));
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(login.failure(new SubmissionError({ _error: data })));
    if (error.response.status === 404) {
      messageError(NO_PERMISSION_MESSAGE);
    } else {
      messageError(data);
    }
  } finally {
    yield put(login.fulfill());
  }
}

export function* verifyUserSessionSaga() {
  try {
    yield put(verifySession.request());
    const client = yield call([SDK, 'getClient']);
    const { data } = yield call([client, 'verifyUserSession']);
    const abilities = [];
    if (data.role.name === 'Admin') {
      abilities.push(
        { action: '*', subject: 'videos' },
        { action: '*', subject: 'library' },
        { action: '*', subject: 'monitoring' }
      );
    } else {
      abilities.push({ action: '*', subject: 'monitoring' });
    }
    const ability = createAbilityForUser(abilities);
    yield put(retrieveMe.success(data));
    yield put(
      verifySession.success({ ability, tokenExpiresAt: data.tokenExpiresAt })
    );
  } catch (error) {
    yield put(verifySession.failure());
    yield put(logout());
  } finally {
    yield put(verifySession.fulfill());
  }
}

/**
 * Hands an expired session off to the accounts app.
 *
 * Only a session that runs out while the app is open comes through here. A 401
 * from the API takes the plain logout path instead, which keeps the user in the
 * app so PrivateRoute can record the deep link they were on.
 */
export function* sessionExpiryWatcherSaga({ payload }) {
  const tokenExpiresAt = payload?.tokenExpiresAt;
  if (!tokenExpiresAt) {
    // The API does not report an expiry time, so there is nothing to watch.
    return;
  }

  const expiresAtMs = new Date(tokenExpiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) {
    return;
  }

  const { expired } = yield race({
    expired: delay(Math.max(expiresAtMs - Date.now(), 0)),
    loggedOut: take([logout.TRIGGER, logout.SUCCESS]),
  });

  if (!expired) {
    return;
  }

  // Read the destination before anything else: clearing the auth state unmounts
  // the page the user was on, and the redirect leaves this origin entirely.
  const returnUrl = yield call(currentUrl);

  // Local teardown still runs so nothing stale rehydrates. Its `userLogOut`
  // request may be cut short by the navigation, which is fine — an expired
  // token has no live server session left to clear.
  yield put(logout());

  // A misconfigured accounts URL makes this throw. Don't strand the user on the
  // dead page: fall back to the in-app login route.
  try {
    yield call(redirectToAccountsLogin, returnUrl);
  } catch (error) {
    messageError('Could not reach the sign-in page. Please sign in again.');
    yield put(push('/'));
  }
}

export function* logoutSaga() {
  try {
    yield put(logout.request());

    const client = yield call([SDK, 'getClient']);
    yield call([client, 'userLogOut']);
    yield put(logout.success());
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(logout.failure(new SubmissionError({ _error: data })));
    messageError(data);
  } finally {
    yield put(logout.fulfill());
    yield put({ type: CLEAR_STORE });
  }
}

export function* forgotPasswordSaga({ payload: { values: requestBody } }) {
  try {
    yield put(forgotPassword.request());
    const client = yield call([SDK, 'getClient']);
    yield call([client, 'forgotPassword'], null, requestBody);
    yield put(forgotPassword.success());
    yield put(closeResetPasswordModal());
    messageSuccess('Email sent successfully');
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(forgotPassword.failure(new SubmissionError({ _error: data })));
    messageError(data);
  } finally {
    yield put(forgotPassword.fulfill());
  }
}

export function* resetPasswordSaga({ payload: { values } }) {
  try {
    yield put(resetPassword.request());
    const client = yield call([SDK, 'getClient']);
    yield call([client, 'resetPassword'], null, values);
    yield put(push('/'));
    messageSuccess('Password successfully changed!');
    yield put(resetPassword.success());
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(resetPassword.failure(new SubmissionError({ _error: data })));
    messageError(data);
  } finally {
    yield put(resetPassword.fulfill());
  }
}

export function* changePasswordSaga({ payload: { values: requestBody } }) {
  try {
    yield put(changePassword.request());
    const { currentPassword, newPassword, confirmNewPassword } = requestBody;

    const user = yield select(getMeItem);
    const client = yield call([SDK, 'getClient']);

    const request = {
      currentPassword,
      newPassword,
      confirmNewPassword,
    };

    yield call([client, 'UserUpdatePassword'], { id: user.id }, request);

    yield put(closeChangePasswordModal());
    yield put(changePassword.success());
    messageSuccess('Password successfully changed!');
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(changePassword.failure(new SubmissionError({ _error: data })));
    messageError(data);
  } finally {
    yield put(changePassword.fulfill());
  }
}

export default function* authWatch() {
  yield takeLatest(login.TRIGGER, loginSaga);
  yield takeLatest(logout.TRIGGER, logoutSaga);
  yield takeLatest(forgotPassword.TRIGGER, forgotPasswordSaga);
  yield takeLatest(resetPassword.TRIGGER, resetPasswordSaga);
  yield takeLatest(changePassword.TRIGGER, changePasswordSaga);
  yield takeLatest(verifySession.TRIGGER, verifyUserSessionSaga);
  yield takeLatest(
    [login.SUCCESS, verifySession.SUCCESS],
    sessionExpiryWatcherSaga
  );
}
